import React, { useEffect } from "react";
import {
  FaLock,
  FaKey,
  FaUserEdit,
  FaUsers,
  FaHome,
  FaIdCard,
  FaExclamationTriangle,
  FaStar,
  FaHistory,
  FaMoneyCheckAlt,
  FaCreditCard,
  FaUniversity,
  FaChartLine,
  FaExchangeAlt,
  FaBan,
  FaFileInvoice,
  FaShieldAlt,
  FaUserSlash,
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link, useLocation } from "react-router-dom";
import share from "../assets/images/share.png";
import { Carousel } from "@mantine/carousel";
import "@mantine/carousel/styles.css";

const SupportCenter = () => {
  const services = [
    {
      title: "Reset 2FA",
      desc: "Reset two-factor authenticator.",
      icon: <FaLock />,
      link: "/reset2fa",
    },
    {
      title: "Reset Password",
      desc: "Change your password.",
      icon: <FaKey />,
      link: "/reset-password",
    },
    {
      title: "Name/Birthday Correction",
      desc: "Correct your name/birthday.",
      icon: <FaUserEdit />,
      link: "/correction",
    },
    {
      title: "Legacy Inheritance",
      desc: "Claim deceased user's assets.",
      icon: <FaUsers />,
      link: "/inheritance",
    },
    {
      title: "Change Residential Address",
      desc: "Change address and provide new Proof of Address documentation.",
      icon: <FaHome />,
    },
    // {
    //   title: "Reset KYC",
    //   desc: "Identity verification update.",
    //   icon: <FaIdCard />,
    // },
    // {
    //   title: "Appeal KYC Rejection",
    //   desc: "Request review of verification rejection.",
    //   icon: <FaExclamationTriangle />,
    // },
    {
      title: "Crypto Deposit Not Arrived",
      desc: "Crypto Deposit not credited to Tillionx account.",
      icon: <FaStar />,
      link: "/crypto-deposit",
    },
    {
      title: "Transaction History",
      desc: "View Transaction History.",
      icon: <FaHistory />,
      link: "/transaction-history",
    },
    {
      title: "Fiat Deposit Not Arrived Appeal",
      desc: "Investigate missing or delayed fiat deposit record.",
      icon: <FaMoneyCheckAlt />,
      link: "/fiat-deposit-appeal",
    },
    {
      title: "Fiat Deposit Suspension",
      desc: "Request review for failed fiat deposit via card.",
      icon: <FaCreditCard />,
    },
    {
      title: "Fiat Withdrawal Not Received",
      desc: "Investigate fiat withdrawal that has not arrived.",
      icon: <FaUniversity />,
      link: "/fiat-withdrawal",
    },
    {
      title: "Appeal P2P Performance Metrics",
      desc: "Revert completion rate/remove negative comment.",
      icon: <FaChartLine />,
      link: "/p2p-performance",
    },
    {
      title: "Refund funds to P2P Counterparty",
      desc: "Refund counterparty to resolve P2P dispute.",
      icon: <FaExchangeAlt />,
      link: "/refund-funds-p2p",
    },
    {
      title: "Lift P2P Suspension",
      desc: "Review inappropriate P2P trade suspensions.",
      icon: <FaBan />,
    },
    {
      title: "P2P Order Receipts",
      desc: "Request P2P order confirmation receipts",
      icon: <FaFileInvoice />,
      link: "/p2p-performance",
    },
    {
      title: "Non-P2P Related Scam Report",
      desc: "Report potential rug pulls or investment scams.",
      icon: <FaShieldAlt />,
    },
    {
      title: "Disable Account",
      desc: "Request to manually disable account",
      icon: <FaUserSlash />,
      link: "/disable-account",
    },
  ];

  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
   
  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
      <Header />
      <div className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          {/* Title */}
          <h2 className="text-lg">Welcome To</h2>
          <h1 className="text-4xl font-bold mt-2">
            Tillionx Help Center <span className="text-yellow-400">🎧</span>
          </h1>

          {/* Search */}
          <div className="mt-6">
            <input
              type="text"
              placeholder="Search or ask question"
              className="w-full max-w-xl mx-auto border border-gray-300 bg-transparent rounded-md py-3 px-4 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        </div>

        {/* Self-Service */}
        <div className="max-w-6xl mx-auto mt-16">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Self-Service</h2>
            {/* <Link to="#" className="hover:text-yellow-500 text-sm">
              More →
            </Link> */}
          </div>

          <Carousel
            // withIndicators
            withControls
            loop
            height="auto"
            align="start"
            slideGap="md"
            slidesToScroll={1}
            breakpoints={[
              { maxWidth: "lg", slideSize: "33.333%" }, // 3 per view
              { maxWidth: "md", slideSize: "50%" }, // 2 per view
              { maxWidth: "sm", slideSize: "100%" }, // 1 per view
            ]}
            slideSize={{ base: "100%", sm: "50%", md: "33.333333%" }}
            className="w-full"
          >
            {services.map((s, idx) => (
              <Carousel.Slide key={idx}>
                <Link
                  to={s.link || "#"}
                  className="border rounded-lg p-5 shadow-sm hover:shadow-md transition cursor-pointer text-left block h-full"
                >
                  <div className="text-2xl mb-3">{s.icon}</div>
                  <h3 className="font-semibold">{s.title}</h3>
                  <p className="text-sm mt-1 font-normal text-slate-500">
                    {s.desc}
                  </p>
                </Link>
              </Carousel.Slide>
            ))}
          </Carousel>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Title */}
        <h2 className="text-2xl font-semibold mb-6">Need More Support?</h2>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Chat Support */}
          <div className="flex justify-between items-center border rounded-xl p-6 shadow-sm hover:shadow-md transition">
            <div>
              <h3 className="font-semibold text-lg mb-2">Chat Support</h3>
              <p className="text-gray-600 text-sm mb-3">
                Feel free to reach out to us if you encounter any issues; our
                chat support team is available 24/7.
              </p>
              <Link
                to="/chat-support"
                className="text-yellow-500 text-sm font-medium hover:underline"
              >
                View more
              </Link>
            </div>
            <img src={share} alt="Chat Support" className="w-16 h-16" />
          </div>

          {/* Product Feedback & Suggestions */}
          <div className="flex justify-between items-center border rounded-xl p-6 shadow-sm hover:shadow-md transition">
            <div>
              <h3 className="font-semibold text-lg mb-2">
                Product Feedback & Suggestions
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Tillionx is always here to listen. Let’s enhance our community
                together.
              </p>
              <Link
                to="#"
                className="text-yellow-500 text-sm font-medium hover:underline"
              >
                View more
              </Link>
            </div>
            <img
              src="https://img.icons8.com/fluency/96/000000/task.png"
              alt="Feedback"
              className="w-16 h-16"
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SupportCenter;
